import { database } from "..";
import Product from "../model/product";
import Proposal from "../model/proposal";
import ProposalLineItem from "../model/proposalLineItem";
import Customer from "../model/customer";
import { QbCustomer, QbProduct, QbProposal } from "../utils/quickbooksExport";

// Everything the QuickBooks export needs, read from the device's database
// in four queries.
export async function loadQuickbooksData(): Promise<{
  customers: QbCustomer[];
  products: QbProduct[];
  proposals: QbProposal[];
}> {
  const [dbCustomers, dbProducts, dbProposals, dbLines] = await Promise.all([
    database.get<Customer>("customers").query().fetch(),
    database.get<Product>("products").query().fetch(),
    database.get<Proposal>("proposals").query().fetch(),
    database.get<ProposalLineItem>("proposal_line_items").query().fetch(),
  ]);

  const linesByProposal = new Map<string, ProposalLineItem[]>();
  for (const l of dbLines) {
    const pid = l.proposal?.id || "";
    linesByProposal.set(pid, [...(linesByProposal.get(pid) || []), l]);
  }
  const ms = (d: unknown) => (d instanceof Date ? d.getTime() : Number(d) || 0);

  return {
    customers: dbCustomers.map((c) => ({
      id: c.id,
      firstName: c.firstName || "",
      lastName: c.lastName || "",
      email: c.email || "",
      phone: c.phone || "",
      addressLine1: c.addressLine1 || "",
      addressLine2: c.addressLine2 || "",
      city: c.city || "",
      state: c.state || "",
      zip: c.zip || "",
      createdAt: ms(c.createdAt),
    })),
    products: dbProducts.map((p) => ({
      id: p.id,
      title: p.title || "",
      description: p.description || "",
      price: p.price || 0,
      taxable: !!p.taxable,
    })),
    proposals: dbProposals.map((p) => ({
      id: p.id,
      customerId: p.customer?.id || "",
      firstName: p.firstName || "",
      lastName: p.lastName || "",
      status: p.status || "",
      invoiceDate: ms(p.invoiceDate) || undefined,
      invoiceDueDate: ms(p.invoiceDueDate) || undefined,
      createdAt: ms(p.createdAt),
      taxRate: Number(p.taxRate) || 0,
      taxAmount: Number(p.taxAmount) || 0,
      totalAmount: Number(p.totalAmount) || 0,
      archivedAt: p.archivedAt || null,
      serviceAddressLine1: p.serviceAddressLine1 || "",
      serviceCity: p.serviceCity || "",
      serviceState: p.serviceState || "",
      serviceZip: p.serviceZip || "",
      lines: (linesByProposal.get(p.id) || [])
        .sort((a, b) => ms(a.createdAt) - ms(b.createdAt))
        .map((l) => ({
          productId: l.product?.id || "",
          productName: l.productName || "",
          productNotes: l.productNotes || "",
          quantity: Number(l.quantity) || 0,
          unitPrice: Number(l.unitPrice) || 0,
        })),
    })),
  };
}
